import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { getManufacturesListingPaginate } from "../../../api/context/manufacturer";

const OrderFilterModal = ({ show, onHide, onApplyFilters, searchFormik, filterData, userType }) => {
  const [activeFilter, setActiveFilter] = useState("Order Status");
  const isAdmin = userType === 'ADMIN';

  const [searchQueries, setSearchQueries] = useState({
    "Order Status": "",
    "Order Type": "",
    "Payment Mode": "",
    "Payment Status": "",
    Vendors: "",
  });

  const [localSelections, setLocalSelections] = useState({
    orderStatus: [],
    orderType: [],
    paymentMode: [],
    paymentStatus: [],
    vendors: [],
    startDate: "",
    endDate: "",
  });

  const [vendorList, setVendorList] = useState([]);

  const orderStatusOptions = [
    { id: "INITIATED", name: "INITIATED" },
    { id: "CONFIRMED", name: "CONFIRMED" },
    { id: "DELIVERED", name: "DELIVERED" },
    { id: "CANCELLED", name: "CANCELLED" },
    { id: "REJECTED", name: "REJECTED" },
  ];

  const paymentModeOptions = [
    { id: "COD", name: "COD" },
    { id: "ONLINE", name: "ONLINE" },
  ];

  const paymentStatusOptions = [
    { id: "PENDING", name: "PENDING" },
    { id: "DONE", name: "DONE" },
  ];

  const orderTypeOptions = [
    { id: null, name: "NEW" },
    { id: 1, name: "RETURN" },
    { id: 2, name: "EXCHANGE" },
  ];

  const filterTypes = [
    { key: "Order Status", stateKey: "orderStatus" },
    { key: "Order Type", stateKey: "orderType" },
    { key: "Payment Mode", stateKey: "paymentMode" },
    { key: "Payment Status", stateKey: "paymentStatus" },
    ...(isAdmin ? [{ key: "Vendors", stateKey: "vendors" }] : []),
    { key: "Date Range", stateKey: "date" },
  ];

  useEffect(() => {
    if (show) {
      if (isAdmin) {
        fetchVendors();
      }

      setLocalSelections({
        orderStatus: filterData?.orderStatus
          ? [{ id: filterData.orderStatus, name: filterData.orderStatus }]
          : [],
        orderType: filterData?.orderType !== null && filterData?.orderType !== undefined && filterData?.orderType !== ""
          ? [{ id: filterData.orderType, name: filterData.selected_orderType?.name || (filterData.orderType === null ? "NEW" : filterData.orderType === 1 ? "RETURN" : "EXCHANGE") }]
          : [],
        paymentMode: filterData?.paymentMode
          ? [{ id: filterData.paymentMode, name: filterData.paymentMode }]
          : [],
        paymentStatus: filterData?.paymentStatus
          ? [{ id: filterData.paymentStatus, name: filterData.paymentStatus }]
          : [],
        vendors: (filterData?.vendors || []).map((id) => ({ id })),
        startDate: filterData?.startDate || "",
        endDate: filterData?.endDate || "",
      });
    }
  }, [show, filterData, isAdmin]);

  const fetchVendors = () => {
    getManufacturesListingPaginate({ page: 1, limit: 100, search: "" })((res) => {
      console.log("Vendor API Response:", res); // 🔍 DEBUG: Check API response
      if (res?.statusCode === 200 && res?.results) {
        const vendors = res.results.map((v) => ({
          id: v.value || v.id,
          name: v.label || v.storeName
        }));
        console.log("Mapped Vendors:", vendors); // 🔍 DEBUG: Check mapped data
        setVendorList(vendors);
      } else {
        console.log("Vendor API Error or Empty:", res);
        setVendorList([]);
      }
    });
  };

  // Single selection for Order Status, Order Type, Payment Mode, Payment Status
  const singleSelectFilters = ["Order Status", "Order Type", "Payment Mode", "Payment Status"];
  const isMultiSelect = !singleSelectFilters.includes(activeFilter);

  const handleSelection = (key, item) => {
    if (isMultiSelect) {
      // Multiple selection (checkbox) - toggle behavior
      const exists = localSelections[key].some((x) => x.id === item.id);
      setLocalSelections((prev) => ({
        ...prev,
        [key]: exists
          ? prev[key].filter((x) => x.id !== item.id)
          : [...prev[key], item],
      }));
    } else {
      // Single selection (radio) - replace all with one
      setLocalSelections((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };

  const getList = () => {
    switch (activeFilter) {
      case "Order Status": return orderStatusOptions;
      case "Order Type": return orderTypeOptions;
      case "Payment Mode": return paymentModeOptions;
      case "Payment Status": return paymentStatusOptions;
      case "Vendors": return vendorList;
      default: return [];
    }
  };

  const handleStartDateChange = (value) => {
    setLocalSelections((prev) => {
      if (value && prev.endDate && value > prev.endDate) {
        return prev;
      }

      return { ...prev, startDate: value };
    });
  };

  const handleEndDateChange = (value) => {
    setLocalSelections((prev) => {
      if (value && prev.startDate && value < prev.startDate) {
        return prev;
      }

      return { ...prev, endDate: value };
    });
  };

  const handleApply = () => {
    const filters = {
      orderStatus: localSelections.orderStatus.map((i) => i.id)[0] || "",
      orderType: localSelections.orderType.length > 0 ? localSelections.orderType[0].id : null,
      paymentMode: localSelections.paymentMode.map((i) => i.id)[0] || "",
      paymentStatus: localSelections.paymentStatus.map((i) => i.id)[0] || "",
      startDate: localSelections.startDate,
      endDate: localSelections.endDate,
    };

    if (isAdmin) {
      filters.vendors = localSelections.vendors.map((i) => i.id);
    }

    onApplyFilters(filters);

    onHide();
  };

  const handleClear = () => {
    setLocalSelections({
      orderStatus: [],
      orderType: [],
      paymentMode: [],
      paymentStatus: [],
      vendors: [],
      startDate: "",
      endDate: "",
    });

    const clearedFilters = {
      orderStatus: "",
      orderType: null,
      paymentMode: "",
      paymentStatus: "",
      startDate: "",
      endDate: "",
    };

    if (isAdmin) {
      clearedFilters.vendors = [];
    }

    onApplyFilters(clearedFilters);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Filters</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="d-flex" style={{ minHeight: 400 }}>

          {/* LEFT */}
          <div className="border-end bg-light" style={{ width: 220 }}>
            {filterTypes.map((f) => (
              <div
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`p-3 ${activeFilter === f.key ? "bg-white fw-bold" : ""}`}
                style={{ cursor: "pointer" }}
              >
                {f.key} {localSelections[f.stateKey]?.length > 0 ? `(${localSelections[f.stateKey].length})` : ""}
              </div>
            ))}
          </div>

          {/* RIGHT */}
          <div className="p-3 flex-grow-1">

            {activeFilter === "Date Range" ? (
              <>
                <h5 className="mb-4 fw-bold">Select Date</h5>
                <div className="mb-3">
                  <label className="form-label fw-bold text-secondary mb-2">Start Date</label>
                  <Form.Control
                    type="date"
                    className="mb-2"
                    value={localSelections.startDate}
                    max={localSelections.endDate || undefined}
                    onChange={(e) =>
                      setLocalSelections((p) => ({
                        ...p,
                        startDate: e.target.value,
                      }))
                    }
                  />

                </div>
                <div>
                  <label className="form-label fw-bold text-secondary mb-2">End Date</label>
                  <Form.Control
                    type="date"
                    value={localSelections.endDate}
                    min={localSelections.startDate || undefined}
                    onChange={(e) =>
                      setLocalSelections((p) => ({
                        ...p,
                        endDate: e.target.value,
                      }))
                    }
                  />
                </div>
              </>
            ) : (
              <>
                <Form.Control
                  placeholder="Search..."
                  className="mb-3"
                  value={searchQueries[activeFilter]}
                  onChange={(e) =>
                    setSearchQueries((p) => ({
                      ...p,
                      [activeFilter]: e.target.value,
                    }))
                  }
                />

                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {getList()
                    .filter((i) =>
                      i.name.toLowerCase().includes(searchQueries[activeFilter].toLowerCase())
                    )
                    .map((item) => {
                      const key = filterTypes.find(f => f.key === activeFilter).stateKey;
                      const checked = localSelections[key].some((x) => x.id === item.id);

                      return (
                        <div
                          key={item.id}
                          onClick={() => handleSelection(key, item)}
                          style={{
                            padding: 10,
                            cursor: "pointer",
                            background: checked ? "#eef2f7" : "",
                          }}
                        >
                          <input
                            type={isMultiSelect ? "checkbox" : "radio"}
                            name={isMultiSelect ? "" : key}
                            checked={checked}
                            readOnly
                            style={{ marginRight: "10px", cursor: "pointer" }}
                          />
                          {item.name}
                        </div>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="link" className="text-dark" onClick={handleClear}>Clear All</Button>
        <Button className="request_btn" onClick={handleApply}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrderFilterModal;
